import { useMemo, useState } from "react";
import { SorterResult, TableRowSelection } from "antd5/es/table/interface";

import { RouterTextLink } from "components/actions/RouterLink";
import { Currency, currencyFormat, numberFormatLong } from "components/app_layout/Typography";
import { commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import { isQueryableSpendFilter, SpendFilters } from "lib/hooks/api/spend/spendFilters";
import { TopSpendBuyer, useTopSpendBuyers } from "lib/hooks/api/spend/useTopSpendBuyers";
import { TopSpendSupplier, useTopSpendSuppliers } from "lib/hooks/api/spend/useTopSpendSuppliers";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import { TypedColumnProps } from "lib/search/SearchTable";
import { SpendSortOrder, SpendSummaryOrderBy } from "lib/types/graphQLEnums";
import { SpendRankingRow } from "../config";
import { MeasureCell } from "../MeasureCell";
import { SpendMeasure, SpendMeasureSchema } from "./useSpendUrlState";

import css from "../Table.module.scss";

export type RankingEntity = "buyers" | "suppliers";

/** How a ranking is ordered: by which measure, and which way. Mirrors the server's orderBy. */
export type SpendRankingSort = {
  measure: SpendMeasure;
  sortOrder: "ASC" | "DESC";
};

export const ORDER_BY: Record<SpendMeasure, SpendSummaryOrderBy> = {
  totalValue: SpendSummaryOrderBy.TotalValue,
  transactionCount: SpendSummaryOrderBy.TransactionCount,
};

export const SORT_ORDER: Record<SpendRankingSort["sortOrder"], SpendSortOrder> = {
  ASC: SpendSortOrder.Asc,
  DESC: SpendSortOrder.Desc,
};

const DEFAULT_COLUMN_SIZE = { large: 240, small: 160, medium: 160, xlarge: 300 };
const NAME_COLUMN_SIZE = { large: 500, small: 200, medium: 400, xlarge: 600 };

/**
 * Reads the ranking sort out of antd's sorter callback. Returns undefined for anything
 * that isn't a measured column, or when antd reports the sort being cleared.
 */
export function rankingSortFromAntSorter<T>(
  sorter: SorterResult<T> | SorterResult<T>[],
): SpendRankingSort | undefined {
  const single = Array.isArray(sorter) ? sorter[0] : sorter;
  if (!single?.order) {
    return undefined;
  }
  const measure = SpendMeasureSchema.safeParse(single.columnKey);
  if (!measure.success) {
    return undefined;
  }
  return { measure: measure.data, sortOrder: single.order === "ascend" ? "ASC" : "DESC" };
}

function antSortOrder(sort: SpendRankingSort, measure: SpendMeasure) {
  if (sort.measure !== measure) {
    return null;
  }
  return sort.sortOrder === "ASC" ? ("ascend" as const) : ("descend" as const);
}

/**
 * The two measured columns of a ranking. The one the ranking is ordered by shows a bar
 * relative to the biggest value on the page. The columns only report a sort change: the
 * buyer and supplier rankings sort on the server, the buyer type ranking in the view.
 */
export function measureColumns<T extends { totalValue: number; transactionCount: number }>(
  rows: T[],
  sort: SpendRankingSort,
): TypedColumnProps<T>[] {
  const maxValue = Math.max(0, ...rows.map((r) => r.totalValue));
  const maxCount = Math.max(0, ...rows.map((r) => r.transactionCount));
  const sortDirections: ("descend" | "ascend")[] = ["descend", "ascend", "descend"];
  return [
    {
      ...commonTableColumns.valueColumn,
      title: "Total value",
      key: "totalValue",
      render: (r) =>
        sort.measure === "totalValue" ? (
          <MeasureCell
            value={r.totalValue}
            maxValue={maxValue}
            formatValue={(value) => currencyFormat(value, "GBP")}
          />
        ) : (
          <Currency value={r.totalValue} />
        ),
      sizeConfig: { large: 320, small: 180, medium: 240, xlarge: 400 },
      sorter: true,
      sortOrder: antSortOrder(sort, "totalValue"),
      sortDirections,
    },
    {
      ...commonTableColumns.valueColumn,
      title: "Transactions",
      key: "transactionCount",
      render: (r) =>
        sort.measure === "transactionCount" ? (
          <MeasureCell
            value={r.transactionCount}
            maxValue={maxCount}
            formatValue={(value) => numberFormatLong(value)}
          />
        ) : (
          numberFormatLong(r.transactionCount)
        ),
      sizeConfig: DEFAULT_COLUMN_SIZE,
      sorter: true,
      sortOrder: antSortOrder(sort, "transactionCount"),
      sortDirections,
    },
  ];
}

const BUYER_COLUMN: TypedColumnProps<SpendRankingRow> = {
  ...commonTableColumns.titleColumn,
  title: "Buyer",
  render: (r) => {
    const buyer = r as TopSpendBuyer;
    return (
      <RouterTextLink to="/buyers/$id" params={{ id: buyer.buyerGuid }}>
        {buyer.buyerName}
      </RouterTextLink>
    );
  },
  sizeConfig: NAME_COLUMN_SIZE,
  key: "buyer",
};

const SUPPLIER_COLUMN: TypedColumnProps<SpendRankingRow> = {
  ...commonTableColumns.titleColumn,
  title: "Supplier",
  width: "33em",
  sizeConfig: NAME_COLUMN_SIZE,
  render: (r) => {
    const supplier = r as TopSpendSupplier;
    return (
      <span className={css.supplierCell}>
        {supplier.supplierGuid ? (
          <RouterTextLink to="/suppliers/$id" params={{ id: supplier.supplierGuid }}>
            {supplier.supplierName}
          </RouterTextLink>
        ) : (
          supplier.supplierName
        )}
      </span>
    );
  },
  key: "supplier",
};

export function rankingRowKey(entity: RankingEntity, row: SpendRankingRow): string {
  if (entity === "buyers") {
    return (row as TopSpendBuyer).buyerGuid;
  }
  const supplier = row as TopSpendSupplier;
  return supplier.supplierGuid ?? supplier.supplierName;
}

export type UseRankingTable = {
  data: SpendRankingRow[];
  isLoading: boolean;
  isError: boolean;
  columns: TypedColumnProps<SpendRankingRow>[];
  rowKey: (r: SpendRankingRow) => string;
  rowSelection: TableRowSelection<SpendRankingRow>;
  pagination: {
    current: number;
    pageSize: number;
    total?: number;
    onChange: (page: number, pageSize?: number) => void;
  };
};

type Options = {
  sort: SpendRankingSort;
  selectedRowKeys: string[];
  onSelectedRowsChange: (keys: string[]) => void;
};

/**
 * Buyers or suppliers ranked by a measure, a page at a time. Owned by the ranking view,
 * alongside the chart grouped the same way. The view is remounted for a new search,
 * grouping or order, which is what sends the ranking back to page one.
 */
export function useRankingTable(
  filters: SpendFilters,
  entity: RankingEntity,
  { sort, selectedRowKeys, onSelectedRowsChange }: Options,
): UseRankingTable {
  const isEnabled = isQueryableSpendFilter(filters);
  const [pageSize, setPageSize] = useTablePageSize("spend-data-table");
  const [currentPage, setCurrentPage] = useState(1);

  const request = {
    filters,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    orderBy: ORDER_BY[sort.measure],
    sortOrder: SORT_ORDER[sort.sortOrder],
  };
  const buyers = useTopSpendBuyers(request, { enabled: isEnabled && entity === "buyers" });
  const suppliers = useTopSpendSuppliers(request, {
    enabled: isEnabled && entity === "suppliers",
  });
  const { data: response, isLoading, isError } = entity === "buyers" ? buyers : suppliers;

  return useMemo(() => {
    const data: SpendRankingRow[] = response?.results ?? [];
    return {
      data,
      isLoading: isEnabled && isLoading,
      isError,
      columns: [
        entity === "buyers" ? BUYER_COLUMN : SUPPLIER_COLUMN,
        ...measureColumns(data, sort),
      ],
      rowKey: (r) => rankingRowKey(entity, r),
      rowSelection: {
        selectedRowKeys,
        onChange: (keys) => onSelectedRowsChange(keys.map(String)),
        preserveSelectedRowKeys: true,
        columnWidth: 48,
        // A supplier we haven't matched to a company can't be saved to a list.
        getCheckboxProps: (r) => ({
          disabled: entity === "suppliers" && !(r as TopSpendSupplier).supplierGuid,
        }),
      },
      pagination: {
        current: currentPage,
        pageSize,
        total: response?.totalResults,
        onChange: (page, newPageSize) => {
          if (newPageSize) setPageSize(newPageSize);
          setCurrentPage(page);
        },
      },
    };
  }, [
    response,
    isEnabled,
    isLoading,
    isError,
    entity,
    sort,
    selectedRowKeys,
    onSelectedRowsChange,
    currentPage,
    pageSize,
    setPageSize,
  ]);
}
